/**
 * MobileBottomNav — barra fija inferior con las tres acciones principales
 * disponibles para el perito en el modo móvil:
 *  · Agenda      → `/m/agenda`      (badge: visitas pendientes hoy)
 *  · Expedientes → `/m/expedientes` (badge: preliminares pendientes)
 *  · Mi control  → `/m/mi-control`  (sin badge)
 *
 * Forzada a una capa GPU dedicada (translate3d + willChange) para que
 * iOS Safari y Chrome Android no la "anclen" a media pantalla durante
 * el momentum-scroll cuando la URL bar se contrae/expande.
 *
 * Polling cada 60s (refresca también al cambiar de ruta y al volver a
 * primer plano) contra `GET /me/mobile-badges` — un único endpoint
 * ligero para los dos contadores.
 */
import { useEffect, useState, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Calendar, Folders, ClockUser,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const TABS = [
  { to: "/m/agenda", label: "Agenda", icon: Calendar, prefix: "/m/agenda", badgeKey: "agenda_pending_today" },
  { to: "/m/expedientes", label: "Expedientes", icon: Folders, prefix: "/m/expedientes", badgeKey: "expedientes_preliminar_pending" },
  { to: "/m/mi-control", label: "Mi control", icon: ClockUser, prefix: "/m/mi-control", badgeKey: null },
];

export default function MobileBottomNav() {
  const { pathname } = useLocation();
  const [badges, setBadges] = useState({
    agenda_pending_today: 0,
    expedientes_preliminar_pending: 0,
  });

  const fetchBadges = useCallback(() => {
    api.get("/me/mobile-badges")
      .then((r) => setBadges(r.data || {}))
      .catch(() => { /* silencioso: no rompe la nav */ });
  }, []);

  // Refresca: al montar, al cambiar de ruta y al volver de background.
  useEffect(() => { fetchBadges(); }, [fetchBadges, pathname]);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") fetchBadges(); };
    document.addEventListener("visibilitychange", onVisible);
    const t = setInterval(fetchBadges, 60_000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(t);
    };
  }, [fetchBadges]);

  return (
    <nav
      style={{ transform: "translate3d(0,0,0)", WebkitTransform: "translate3d(0,0,0)", willChange: "transform" }}
      className="fixed left-0 right-0 bottom-0 z-50 bg-white border-t border-[#E8EAED] shadow-[0_-2px_8px_rgba(0,0,0,0.04)]"
      data-testid="mobile-bottom-nav"
    >
      <div className="grid grid-cols-3 max-w-md mx-auto">
        {TABS.map(({ to, label, icon: Icon, prefix, badgeKey }) => {
          const active = pathname === to || pathname.startsWith(`${prefix}/`) || pathname === prefix;
          const count = badgeKey ? Number(badges[badgeKey] || 0) : 0;
          const slug = label.toLowerCase().replace(" ", "-");
          return (
            <NavLink
              key={to}
              to={to}
              data-testid={`mobile-tab-${slug}`}
              className={`relative flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10.5px] font-semibold ${
                active ? "text-foreground" : "text-zinc-500"
              }`}
            >
              <span className="relative inline-flex">
                <Icon
                  className={`w-5 h-5 ${active ? "text-amber-600" : "text-zinc-500"}`}
                  weight={active ? "fill" : "regular"}
                />
                {count > 0 && (
                  <span
                    data-testid={`mobile-tab-badge-${slug}`}
                    className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold leading-none tabular-nums ring-2 ring-white"
                  >
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </span>
              <span>{label}</span>
              {active && <span className="block w-6 h-[2px] bg-amber-500 rounded-full" />}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
